import PaginationOptions from '../../../../api/typedefs/sdk/PaginationOptions';
import Response from '../../../../api/types/Response';

import getHelper from '../../../services/getHelper';
import Playlist from '../../product/types/Playlist';
import SuggestedCourse from '../../product/types/SuggestedCourse';
import Invitation from '../types/Invitation';

import AnalyticsHelper from './AnalyticsHelper';
import withBulkedInvitations from './withBulkedInvitations';

/**
 * @desc Helper for teams.
 */
class TeamsHelper extends getHelper('/teams/:id', {
  analytics: AnalyticsHelper,
  users: getHelper('/users/:id', {
    roles: getHelper('/roles/:id'),
  }),
  invitations: getHelper<Invitation>('/invitations'),
  playlists: getHelper<Playlist>('/playlists'),
  suggestedCourses: getHelper<SuggestedCourse>('/suggested-courses'),
}) {
  /**
   * @desc Get invitations of given team.
   * @param {Number} teamId Id of the team to get invitations.
   * @param {PaginationOptions} [filters={}] Filters to use to find invitations.
   * @return {Promise<Array<Invitation>>} The promise handling the request.
   * @example
   * const teamId = 1234;
   * const response = await myClient.helpers.employer.teams.getInvitations(1234, {
   *   start: 0,
   *   end: 4,
   *   status: 'pending',
   * });
   */
  getInvitations(teamId: number, filters: PaginationOptions): Promise<Response<Invitation[]>> {
    const request = super.find(filters, false).setRouteParam('id', `${teamId}`);

    request.url += '/invitations';

    return request.execute();
  }

  /**
   * @desc Send new invitations for given team.
   * @param {Number} teamId Id of the team to create new invitations.
   * @param {Array<String>} emails List of invited emails.
   * @return {Promise} The promise handling the request.
   * @example
   * const teamId = 1234;
   * const response = await myClient.helpers.employer.teams.sendInvitations(teamId, [
   *  'foo@bar.com',
   *  'bar@foo.fr',
   * ]);
   */
  sendInvitations(teamId: number, emails: string[]): Promise<Response> {
    const request = super.create({ emails }, false);
    request.url += '/invitations';
    request.setRouteParam('id', `${teamId}`);

    return request.execute();
  }
}

export default withBulkedInvitations(TeamsHelper);
